import callbackRegistry, { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42 } from "../../glue";
import { default as windowStore } from "./store";
import { Executor, GroupInternal, GroupWrapper, WindowWrapper } from "./types";
import { isBoolean, isNullOrWhiteSpace, isUndefinedOrNull } from "../activity/helpers/util";
import utils from "../utils";

export default (id: string, executor: Executor): GroupWrapper => {

    const _registry: CallbackRegistry = callbackRegistry();

    const _windowsId: string[] = [];

    let _isHibernatedFlag: boolean;
    let _isVisible: boolean;

    // #region "API functions"

    async function addWindow(winId: string): Promise<void> {
        if (_windowsId.indexOf(winId) === -1) {
            _windowsId.push(winId);
            const win: WindowWrapper = windowStore.get(winId);
            win.Events.handleGroupChanged(groupObject, undefined);
            _isHibernatedFlag = win.GroupCreationArgs.isGroupHibernated ?? _isHibernatedFlag ?? false;
            _isVisible = win.GroupCreationArgs.isGroupVisible ?? _isVisible ?? true;
            await executor.finished;
            _registry.execute("window-added", groupObject, win.API);
        }
    }

    async function removeWindow(win: WindowWrapper): Promise<void> {
        const index: number = _windowsId.indexOf(win.API.id);
        if (index !== -1) {
            _windowsId.splice(index, 1);
            win.Events.handleGroupChanged(undefined, groupObject);
            await executor.finished;
            _registry.execute("window-removed", groupObject, win.API);
        }
    }

    function find(window: string | Glue42.Windows.GDWindow, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Glue42.Windows.GDWindow {
        let winId: string;
        if (typeof window === "string") {
            winId = window;
        } else if (!isUndefinedOrNull(window)) {
            winId = window.id;
        }

        const win = _mapToWindowObject(winId);
        if (win) {
            if (typeof success === "function") {
                success(win);
            }
            return win;
        } else {
            if (typeof error === "function") {
                error(`No window with ID: ${winId}`);
            }
        }
    }

    function windows(success?: (windows: Glue42.Windows.GDWindow[]) => void): Glue42.Windows.GDWindow[] {
        const mappedWindows: Glue42.Windows.GDWindow[] = _mapToWindowObjects();
        if (typeof success === "function") {
            success(mappedWindows);
        }
        return mappedWindows;
    }

    async function execute(command: string, options: any & { windowId: string }, ...keys: string[]): Promise<Glue42.Windows.Group> {
        await executor.execute<Glue42.Windows.Group>(command, options, ...keys);
        return groupObject;
    }

    // #endregion "API functions"

    // #region "Stream function"

    function handleGroupHeaderVisibilityChanged(windowInfo: any): void {
        _registry.execute("header-visibility-changed", groupObject);
    }

    function handleGroupVisibilityChanged(visibile: boolean): void {
        _isVisible = visibile;
        _registry.execute("group-visibility-changed", groupObject);
    }

    function handleGroupHibernateChanged(isHibernatedFlag: boolean): void {
        _isHibernatedFlag = isHibernatedFlag;
    }

    // #endregion "Stream function"

    // #region "Internal function"

    function _mapToWindowObjects(): Glue42.Windows.GDWindow[] {
        const winObjects: Glue42.Windows.GDWindow[] = [];

        _windowsId.forEach((winId) => {
            const windowObject = _mapToWindowObject(winId);
            if (typeof windowObject !== "undefined") {
                winObjects.push(windowObject);
            }
        });

        return winObjects;
    }

    function _mapToWindowObject(windowId: string): Glue42.Windows.GDWindow {
        return windowStore.get(windowId) ? windowStore.get(windowId).API : undefined;
    }

    function _getGroupHeaderVisibility(): boolean {
        // if one of the windows has a flag hideGroupHeader the group header should be off.
        const windowWithHiddenHeader = _mapToWindowObjects().find((w) => !w.isGroupHeaderVisible);
        const _isGroupHeaderVisible = windowWithHiddenHeader === undefined;

        return _isGroupHeaderVisible;
    }

    function isHibernated(): boolean {
        return _isHibernatedFlag;
    }

    // #endregion "Internal function"

    // #region "Events"

    function onHeaderVisibilityChanged(callback?: (group: Glue42.Windows.Group) => void): () => void {
        return _registry.add("header-visibility-changed", callback);
    }

    function onWindowAdded(callback?: (group: Glue42.Windows.Group, window: Glue42.Windows.GDWindow) => void): () => void {
        return _registry.add("window-added", callback);
    }

    function onWindowRemoved(callback?: (group: Glue42.Windows.Group, window: Glue42.Windows.GDWindow) => void): () => void {
        return _registry.add("window-removed", callback);
    }

    function onVisibilityChanged(callback?: (group: Glue42.Windows.Group) => void): () => void {
        if (!callback) {
            throw new Error("Callback argument is required");
        }
        if (callback && typeof callback !== "function") {
            throw new Error("Callback argument must be a function")
        }
        return _registry.add("group-visibility-changed", callback);
    }

    function onClosing(callback: (prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => Promise<void>): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("callback must be a function");
        }

        type GDHandler = (success: () => void, error: () => void, prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => void;
        const callbackWrap: GDHandler = (success: () => void, error: () => void, prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => {
            const promise = callback(prevent);
            if (promise?.then) {
                promise.then(success).catch(error);
            } else {
                success();
            }
        };
        return executor.onGroupClosing(callbackWrap, groupObject);
    }

    // #endregion "Events"

    // #region "API"

    const groupObject: Glue42.Windows.Group = {
        id,
        get windows() {
            return windows();
        },
        find,
        get isHeaderVisible() {
            return _getGroupHeaderVisibility();
        },
        get isHibernated() {
            return isHibernated();
        },
        get isVisible() {
            return _isVisible;
        },
        showHeader: (success, error) => {
            return utils.callbackifyPromise(() => {
                return execute("setGroupHeaderVisibility", { windowId: _windowsId[0], options: { toShow: true } }, ..._windowsId.map((w) => `GroupHeaderVisibilityChanged-${w}`));
            }, success, error);
        },
        hideHeader: (success, error) => {
            return utils.callbackifyPromise(() => {
                return execute("setGroupHeaderVisibility", { windowId: _windowsId[0], options: { toShow: false } }, ..._windowsId.map((w) => `GroupHeaderVisibilityChanged-${w}`));
            }, success, error);
        },
        getTitle: async () => {
            const r = await executor.execute<{ title: string }>("getGroupTitle", { windowId: _windowsId[0] });
            return r.title;
        },
        setTitle: async (title: string) => {
            if (isNullOrWhiteSpace(title)) {
                throw new Error("`title` must not be null or undefined.");
            }
            return execute("setGroupTitle", { windowId: _windowsId[0], options: { title } });
        },
        capture: (captureOptions: Glue42.Windows.CaptureOptions) => {
            return executor.captureGroup(_windowsId, captureOptions);
        },
        maximize: (success, error) => {
            return utils.callbackifyPromise(() => {
                return execute("maximizeGroup", { windowId: _windowsId[0] }, ..._windowsId.map((w) => `StateChanged-${w}`));
            }, success, error);
        },
        restore: (success, error) => {
            return utils.callbackifyPromise(() => {
                return execute("restoreGroup", { windowId: _windowsId[0] }, ..._windowsId.map((w) => `StateChanged-${w}`));
            }, success, error);
        },
        show: (activate?: boolean) => {
            if (!isUndefinedOrNull(activate) && !isBoolean(activate)) {
                throw new Error("Activate flag must be a boolean!");
            }
            activate = !isUndefinedOrNull(activate) ? activate : true;
            return executor.executeGroup("showGroup", {
                groupId: id,
                options: { activate }
            });
        },
        hide: () => {
            return executor.executeGroup("hideGroup", {
                groupId: id
            });
        },
        onHeaderVisibilityChanged,
        onWindowAdded,
        onWindowRemoved,
        onVisibilityChanged,
        onClosing,
    };

    const internal: GroupInternal = {
        get windows() {
            return _windowsId;
        },
        addWindow,
        removeWindow,
        handleGroupHeaderVisibilityChanged,
        handleGroupVisibilityChanged,
        handleGroupHibernateChanged
    };

    return {
        groupAPI: groupObject,
        groupInternal: internal,
    };

    // #endregion "API"
};
